
'use client';

import * as React from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Images as ImagesIcon, Video, ChevronLeft, ChevronRight, Loader2, Youtube, AlertTriangle } from 'lucide-react';
// ... imports
import {
  getHybridRootAlbums,
  getHybridSubAlbums,
  getHybridAlbumMedia,
  type HybridAlbum,
  type HybridMediaItem
} from '@/lib/gallery-hybrid';
import { useCollection, useFirestore } from '@/firebase';
import { collection, doc, getDoc } from 'firebase/firestore';
import Image from 'next/image';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
} from '@/components/ui/dialog';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import type { YouTubeVideo } from '@/models/video';
import { useToast } from '@/hooks/use-toast';

const MainTabs = ({ activeTab }: { activeTab: string }) => (
  <TabsList className="grid w-full grid-cols-2 mb-8">
    <TabsTrigger value="photos" asChild>
      <Link href="/gallery/photos">
        <ImagesIcon className="mr-2" />
        Photos
      </Link>
    </TabsTrigger>
    <TabsTrigger value="videos" asChild>
      <Link href="/gallery/videos">
        <Video className="mr-2" />
        Videos
      </Link>
    </TabsTrigger>
  </TabsList>
);

const VideosTab = () => {
  const firestore = useFirestore();
  const videosRef = React.useMemo(() => {
    if (!firestore) return null;
    return collection(firestore, 'gallery/videoSources/videos');
  }, [firestore]);
  const { data: videos, loading, error } = useCollection(videosRef);
  const { toast } = useToast();

  React.useEffect(() => {
    if (error) {
      toast({
        variant: 'destructive',
        title: 'Error loading videos',
        description: error.message,
      });
    }
  }, [error, toast]);

  if (loading) {
    return (
      <div className="flex justify-center items-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <p className="ml-4 text-muted-foreground">Loading Videos...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center flex-col gap-4 text-center py-20 text-destructive">
        <AlertTriangle className="w-8 h-8" />
        <p>Videos are temporarily unavailable. Please try again later.</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {videos && videos.map((video) => (
        <Card key={video.id}>
          <CardHeader>
            <CardTitle>{video.title}</CardTitle>
            <CardDescription>
              By: <a href={video.channelUrl} target="_blank" rel="noopener noreferrer" className="hover:underline flex items-center gap-1">
                <Youtube className="w-4 h-4" />
                {video.channelName}
              </a>
            </CardDescription>
          </CardHeader>
          <CardContent>
            {video.sourceType === 'channel_streams' ? (
              <a
                href={video.sourceUrl || video.embedUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center aspect-video w-full overflow-hidden rounded-lg border bg-muted hover:bg-muted/80 transition-colors"
              >
                <div className="text-center p-8">
                  <Youtube className="w-12 h-12 mx-auto mb-4 text-primary" />
                  <p className="font-semibold mb-2">View Channel Streams</p>
                  <p className="text-sm text-muted-foreground">Click to open in YouTube</p>
                </div>
              </a>
            ) : (
              <div className="aspect-video w-full overflow-hidden rounded-lg border">
                <iframe
                  src={video.embedUrl}
                  width="100%"
                  height="100%"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  title={video.title}
                  className="border-0"
                ></iframe>
              </div>
            )}
          </CardContent>
        </Card>
      ))}
      {(!videos || videos.length === 0) && (
        <div className="text-center py-20 text-muted-foreground md:col-span-2">
          <p>No videos have been added yet.</p>
        </div>
      )}
    </div>
  );
};

export default function Gallery({
  mainTab: initialMainTab = 'photos',
}: {
  mainTab?: string;
}) {
  const firestore = useFirestore();
  const pathname = usePathname();
  const pathSegments = pathname.split('/').filter(Boolean);

  const mainTab = pathSegments[1] || initialMainTab;
  const currentAlbumId = pathSegments[2];

  const [currentAlbum, setCurrentAlbum] = React.useState<HybridAlbum | null>(null);
  const [subAlbums, setSubAlbums] = React.useState<HybridAlbum[]>([]);
  const [mediaItems, setMediaItems] = React.useState<HybridMediaItem[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  const [selectedImageIndex, setSelectedImageIndex] = React.useState<number | null>(null);

  React.useEffect(() => {
    const fetchGalleryData = async () => {
      if (!firestore) return;

      setLoading(true);
      setError(null);

      try {
        if (!currentAlbumId) {
          // Root View
          const rootAlbums = await getHybridRootAlbums(firestore);
          setCurrentAlbum(null); // No specific album selected
          setSubAlbums(rootAlbums);
          setMediaItems([]);
        } else {
          // Album View
          // 1. Fetch Album Details
          const albumRef = doc(firestore, 'albums', currentAlbumId);
          const albumSnap = await getDoc(albumRef);

          if (!albumSnap.exists()) {
            throw new Error('Album not found');
          }

          const albumData = { id: albumSnap.id, ...albumSnap.data() } as any;

          const navAlbum: HybridAlbum = {
            id: albumData.id,
            name: albumData.name,
            description: albumData.description,
            coverUrl: albumData.coverUrl,
            mediaCount: albumData.mediaCount || 0,
            source: 'firebase',
            path: albumData.albumPath,
            parentId: albumData.parentAlbumId,
            depth: albumData.depth || 0,
            albumType: albumData.albumType,
            visibility: albumData.visibility
          };

          setCurrentAlbum(navAlbum);

          // 2. Fetch Content
          const [subs, media] = await Promise.all([
            getHybridSubAlbums(firestore, currentAlbumId),
            getHybridAlbumMedia(firestore, currentAlbumId)
          ]);

          setSubAlbums(subs);
          setMediaItems(media);
        }
      } catch (err) {
        console.error(err);
        setError('Failed to load gallery data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    if (mainTab === 'photos') {
      fetchGalleryData();
    } else {
      // If we're on the videos tab, stop loading and clear photo-specific data
      setLoading(false);
      setCurrentAlbum(null);
      setError(null);
    }
  }, [currentAlbumId, mainTab, firestore]);

  const handleImageClick = (index: number) => {
    setSelectedImageIndex(index);
  };

  const handleClose = () => {
    setSelectedImageIndex(null);
  }

  const handleNext = () => {
    if (selectedImageIndex !== null && mediaItems && mediaItems.length > 0) {
      setSelectedImageIndex((selectedImageIndex + 1) % mediaItems.length);
    }
  };

  const handlePrev = () => {
    if (selectedImageIndex !== null && mediaItems && mediaItems.length > 0) {
      setSelectedImageIndex((selectedImageIndex - 1 + mediaItems.length) % mediaItems.length);
    }
  };

  const selectedImage = selectedImageIndex !== null && mediaItems ? mediaItems[selectedImageIndex] : null;

  const galleryDescription =
    mainTab === 'videos'
      ? 'A collection of videos from our club members.'
      : currentAlbum
        ? currentAlbum.description || currentAlbum.name
        : loading
          ? 'Loading gallery...'
          : 'A collection of photos from our club members.';

  const shouldShowSubAlbums = !loading && subAlbums && subAlbums.length > 0 && mediaItems.length === 0;

  return (
    <div className="w-full max-w-7xl mx-auto p-4 md:p-8">
      <Card className="mb-8">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-3xl">
            <ImagesIcon className="w-8 h-8 text-primary" />
            Club Gallery
          </CardTitle>
          <CardDescription>
            {galleryDescription}
          </CardDescription>
        </CardHeader>
      </Card>

      <Tabs defaultValue={mainTab} value={mainTab} className="w-full">
        <MainTabs activeTab={mainTab} />

        <TabsContent value="photos">
          {loading ? (
            <div className="flex justify-center items-center py-20">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="ml-4 text-muted-foreground">Loading Gallery...</p>
            </div>
          ) : error ? (
            <div className="flex justify-center items-center flex-col gap-4 text-center py-20 text-destructive">
              <AlertTriangle className="w-8 h-8" />
              <p>{error}</p>
            </div>
          ) : (
            <div>
              {currentAlbum?.parentId && (
                <Button asChild variant="outline" className="mb-8">
                  <Link href={`/gallery/photos/${currentAlbum.parentId}`}>
                    <ChevronLeft className='mr-2' /> Back to Parent Album
                  </Link>
                </Button>
              )}
              {!currentAlbum?.parentId && currentAlbumId && (
                <Button asChild variant="outline" className="mb-8">
                  <Link href="/gallery/photos">
                    <ChevronLeft className='mr-2' /> Back to All Albums
                  </Link>
                </Button>
              )}

              {shouldShowSubAlbums ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
                  {subAlbums.map((album) => <AlbumCard key={album.id} album={album} />)}
                </div>
              ) : (
                <div className="columns-1 sm:columns-2 md:columns-3 lg:columns-4 gap-4 space-y-4">
                  {mediaItems && mediaItems.map((img, index) => {
                    return (
                      <div
                        key={img.id}
                        className="overflow-hidden rounded-lg break-inside-avoid group cursor-pointer"
                        onClick={() => handleImageClick(index)}
                      >
                        <Image
                          src={img.thumbnailUrl || img.url}
                          alt={img.description || img.title}
                          width={img.width || 200}
                          height={img.height || 200}
                          className="w-full h-auto transform transition-transform duration-300 group-hover:scale-105"
                        />
                      </div>
                    )
                  })}
                </div>
              )}

              {!loading && !error && mediaItems.length === 0 && !shouldShowSubAlbums && (
                <div className="text-center py-20 text-muted-foreground">
                  <p>No images in this album yet.</p>
                </div>
              )}
            </div>
          )}
        </TabsContent>

        <TabsContent value="videos">
          <VideosTab />
        </TabsContent>
      </Tabs>

      <Dialog
        open={selectedImage !== null}
        onOpenChange={(isOpen) => !isOpen && handleClose()}
      >
        <DialogContent className="max-w-5xl w-full h-[90vh] bg-transparent border-0 p-0 flex items-center justify-center">
          {selectedImage && (
            <div className="relative w-full h-full flex items-center justify-center">
              <Button variant="ghost" size="icon" className="absolute left-2 top-1/2 -translate-y-1/2 z-10 text-white bg-black/30 hover:bg-black/50 h-10 w-10" onClick={handlePrev}>
                <ChevronLeft className="h-6 w-6" />
              </Button>

              <div className="relative w-full h-full">
                <Image
                  src={selectedImage.url}
                  alt={selectedImage.description || selectedImage.title}
                  fill
                  className="object-contain"
                />
              </div>

              <Button variant="ghost" size="icon" className="absolute right-2 top-1/2 -translate-y-1/2 z-10 text-white bg-black/30 hover:bg-black/50 h-10 w-10" onClick={handleNext}>
                <ChevronRight className="h-6 w-6" />
              </Button>

              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/50 text-white p-2 rounded-md text-center">
                <p className="text-sm font-semibold">{selectedImage.description || selectedImage.title}</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
